import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from '@/utils/toast';
import { templatesApi } from '@/api/endpoints';
import { Card, Button, Input } from '@azyntor-labs/nexus-ui';

interface TemplateSettings {
  duration_minutes: number;
  passing_score: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  proctoring_enabled: boolean;
  proctoring_settings?: {
    webcam_required: boolean;
    microphone_required: boolean;
    screen_recording: boolean;
    tab_switch_detection: boolean;
    copy_paste_detection: boolean;
    right_click_disabled: boolean;
    keyboard_shortcuts_disabled: boolean;
    face_detection: boolean;
    multiple_faces_detection: boolean;
    gaze_detection: boolean;
    phone_detection: boolean;
    vm_detection: boolean;
    max_violations: number;
    warning_threshold: number;
    auto_terminate: boolean;
  };
  max_attempts: number;
  allow_retake: boolean;
  show_results: boolean;
  allow_review: boolean;
  auto_grade: boolean;
  allow_late_submit: boolean;
  auto_submit_on_timeout: boolean;
  allow_backtracking: boolean;
  require_fullscreen: boolean;
  browser_lockdown: boolean;
  negative_marking: boolean;
  negative_marking_percentage: number;
  certificate_enabled: boolean;
  [key: string]: any;
}

const defaultSettings = (): TemplateSettings => ({
  duration_minutes: 60,
  passing_score: 60,
  shuffle_questions: false,
  shuffle_options: false,
  proctoring_enabled: false,
  proctoring_settings: {
    webcam_required: false,
    microphone_required: false,
    screen_recording: false,
    tab_switch_detection: false,
    copy_paste_detection: false,
    right_click_disabled: false,
    keyboard_shortcuts_disabled: false,
    face_detection: false,
    multiple_faces_detection: false,
    gaze_detection: false,
    phone_detection: false,
    vm_detection: false,
    max_violations: 3,
    warning_threshold: 2,
    auto_terminate: true,
  },
  max_attempts: 1,
  allow_retake: false,
  show_results: true,
  allow_review: true,
  auto_grade: true,
  allow_late_submit: false,
  auto_submit_on_timeout: true,
  allow_backtracking: true,
  require_fullscreen: false,
  browser_lockdown: false,
  negative_marking: false,
  negative_marking_percentage: 25,
  certificate_enabled: false,
});

const defaultProctoringSettings = () => ({
  webcam_required: false,
  microphone_required: false,
  screen_recording: false,
  tab_switch_detection: false,
  copy_paste_detection: false,
  right_click_disabled: false,
  keyboard_shortcuts_disabled: false,
  face_detection: false,
  multiple_faces_detection: false,
  gaze_detection: false,
  phone_detection: false,
  vm_detection: false,
  max_violations: 3,
  warning_threshold: 2,
  auto_terminate: true,
});

export const ExamTemplateEditorPage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!templateId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [settings, setSettings] = useState<TemplateSettings>(defaultSettings());

  // Fetch existing template for edit mode
  const { data: templateRes, isLoading: loadingTemplate } = useQuery({
    queryKey: ['exam-template', templateId],
    queryFn: () => templatesApi.getById(templateId!),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (templateRes?.data?.data) {
      const t = templateRes.data.data;
      setName(t.name || '');
      setDescription(t.description || '');
      setCategory(t.category || '');
      setSettings({ ...defaultSettings(), ...t.settings });
    }
  }, [templateRes]);

  const createMutation = useMutation({
    mutationFn: (data: any) => templatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam-templates'] });
      queryClient.invalidateQueries({ queryKey: ['exam-template-stats'] });
      toast.success('Template created');
      navigate('/teacher/templates');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create template');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => templatesApi.update(templateId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam-templates'] });
      queryClient.invalidateQueries({ queryKey: ['exam-template-stats'] });
      toast.success('Template updated');
      navigate('/teacher/templates');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update template');
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }

    const payload = {
      name,
      description,
      category: category || undefined,
      settings,
    };

    if (isEditMode) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isEditMode && loadingTemplate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/teacher/templates')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white/90">
              {isEditMode ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-sm text-white/50 mt-1">
              {isEditMode ? 'Modify your exam template settings' : 'Set up a reusable exam configuration'}
            </p>
          </div>
        </div>
        <Button
          leftIcon={<Save className="w-4 h-4" />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update Template' : 'Create Template'}
        </Button>
      </div>

      {/* Template Details */}
      <Card>
        <div className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Template Details</h3>
          <Input
            label="Template Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Standard Proctored Exam"
          />
          <div>
            <label className="block text-sm text-white/70 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white/90 placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/50"
              rows={3}
              placeholder="Describe what this template is for..."
            />
          </div>
          <Input
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g., Midterm, Final, Quiz, Assessment"
          />
        </div>
      </Card>

      {/* Basic Settings */}
      <Card>
        <div className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Basic Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Duration (minutes)"
              type="number"
              value={String(settings.duration_minutes)}
              onChange={(e) => setSettings({ ...settings, duration_minutes: Number(e.target.value) })}
            />
            <Input
              label="Passing Score (%)"
              type="number"
              value={String(settings.passing_score)}
              onChange={(e) => setSettings({ ...settings, passing_score: Number(e.target.value) })}
            />
            <Input
              label="Max Attempts"
              type="number"
              value={String(settings.max_attempts)}
              onChange={(e) => setSettings({ ...settings, max_attempts: Number(e.target.value) })}
            />
          </div>
        </div>
      </Card>

      {/* Exam Options */}
      <Card>
        <div className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Exam Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {([
              ['shuffle_questions', 'Shuffle Questions', 'Randomize question order for each student'],
              ['shuffle_options', 'Shuffle Options', 'Randomize answer option order'],
              ['show_results', 'Show Results', 'Show results to students after submission'],
              ['allow_review', 'Allow Review', 'Let students review their answers after grading'],
              ['auto_grade', 'Auto Grade', 'Automatically grade objective questions'],
              ['allow_retake', 'Allow Retakes', 'Students can retake the exam'],
              ['allow_late_submit', 'Allow Late Submit', 'Accept submissions after deadline'],
              ['auto_submit_on_timeout', 'Auto Submit on Timeout', 'Automatically submit when time runs out'],
              ['allow_backtracking', 'Allow Backtracking', 'Students can go back to previous questions'],
              ['require_fullscreen', 'Require Fullscreen', 'Force fullscreen mode during exam'],
              ['browser_lockdown', 'Browser Lockdown', 'Restrict browser functionality'],
              ['negative_marking', 'Negative Marking', 'Deduct marks for wrong answers'],
              ['certificate_enabled', 'Enable Certificate', 'Issue certificate on passing'],
            ] as const).map(([key, label, desc]) => (
              <label
                key={key}
                className="flex items-start justify-between p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex-1 mr-3">
                  <span className="text-sm text-white/80 block">{label}</span>
                  <span className="text-xs text-white/40 block mt-0.5">{desc}</span>
                </div>
                <input
                  type="checkbox"
                  checked={!!settings[key]}
                  onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                  className="w-4 h-4 mt-0.5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer flex-shrink-0"
                />
              </label>
            ))}
          </div>

          {settings.negative_marking && (
            <div className="pt-2">
              <Input
                label="Negative Marking Percentage (%)"
                type="number"
                value={String(settings.negative_marking_percentage)}
                onChange={(e) => setSettings({ ...settings, negative_marking_percentage: Number(e.target.value) })}
                className="max-w-xs"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Proctoring Settings */}
      <Card>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Proctoring Settings</h3>
            <label className="flex items-center space-x-2 cursor-pointer">
              <span className="text-sm text-white/70">Enable Proctoring</span>
              <input
                type="checkbox"
                checked={settings.proctoring_enabled}
                onChange={(e) => setSettings({
                  ...settings,
                  proctoring_enabled: e.target.checked,
                  proctoring_settings: e.target.checked
                    ? (settings.proctoring_settings || defaultProctoringSettings())
                    : settings.proctoring_settings,
                })}
                className="w-4 h-4 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer"
              />
            </label>
          </div>

          {settings.proctoring_enabled && settings.proctoring_settings && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {([
                  ['webcam_required', 'Webcam Required', 'Require webcam access'],
                  ['microphone_required', 'Microphone Required', 'Require microphone access'],
                  ['screen_recording', 'Screen Recording', 'Record student screen'],
                  ['tab_switch_detection', 'Tab Switch Detection', 'Detect tab/window switches'],
                  ['copy_paste_detection', 'Copy/Paste Detection', 'Detect copy/paste actions'],
                  ['right_click_disabled', 'Disable Right Click', 'Block right-click menu'],
                  ['keyboard_shortcuts_disabled', 'Disable Shortcuts', 'Block keyboard shortcuts'],
                  ['face_detection', 'Face Detection', 'Verify student face via webcam'],
                  ['multiple_faces_detection', 'Multiple Faces', 'Detect multiple people'],
                  ['gaze_detection', 'Gaze Detection', 'Track eye movement'],
                  ['phone_detection', 'Phone Detection', 'Detect phone usage'],
                  ['vm_detection', 'VM Detection', 'Detect virtual machines'],
                ] as const).map(([key, label, desc]) => (
                  <label
                    key={key}
                    className="flex items-start justify-between p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="flex-1 mr-3">
                      <span className="text-sm text-white/80 block">{label}</span>
                      <span className="text-xs text-white/40 block mt-0.5">{desc}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!(settings.proctoring_settings as any)?.[key]}
                      onChange={(e) => setSettings({
                        ...settings,
                        proctoring_settings: {
                          ...settings.proctoring_settings!,
                          [key]: e.target.checked,
                        },
                      })}
                      className="w-4 h-4 mt-0.5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer flex-shrink-0"
                    />
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <Input
                  label="Max Violations"
                  type="number"
                  value={String(settings.proctoring_settings.max_violations)}
                  onChange={(e) => setSettings({
                    ...settings,
                    proctoring_settings: {
                      ...settings.proctoring_settings!,
                      max_violations: Number(e.target.value),
                    },
                  })}
                />
                <Input
                  label="Warning Threshold"
                  type="number"
                  value={String(settings.proctoring_settings.warning_threshold)}
                  onChange={(e) => setSettings({
                    ...settings,
                    proctoring_settings: {
                      ...settings.proctoring_settings!,
                      warning_threshold: Number(e.target.value),
                    },
                  })}
                />
                <label className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors self-end">
                  <span className="text-sm text-white/80">Auto Terminate</span>
                  <input
                    type="checkbox"
                    checked={settings.proctoring_settings.auto_terminate}
                    onChange={(e) => setSettings({
                      ...settings,
                      proctoring_settings: {
                        ...settings.proctoring_settings!,
                        auto_terminate: e.target.checked,
                      },
                    })}
                    className="w-4 h-4 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer"
                  />
                </label>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Bottom save bar */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="secondary" onClick={() => navigate('/teacher/templates')}>
          Cancel
        </Button>
        <Button
          leftIcon={<Save className="w-4 h-4" />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
};
